import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toInputDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import PortalAccessSection from "@/components/PortalAccessSection";
import EmployeeDocumentsSection from "@/components/EmployeeDocumentsSection";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, Lock, Unlock, CheckCircle2, ExternalLink, UserMinus } from "lucide-react";

const SHIFT_OPTIONS = [8.5, 9.0, 9.5, 12.0];
const STATUS_OPTIONS = ["Working", "Left", "Rejoin", "Long Holiday"];

const empty = {
  punch_id: "",
  employee_code: "",
  biometric_id: "",
  name: "",
  father_husband_name: "",
  permanent_address: "",
  communication_address: "",
  mobile: "",
  dob: "",
  emergency_contact_person: "",
  emergency_contact_relation: "",
  emergency_contact_number: "",
  aadhaar_no: "",
  pan_no: "",
  unit_id: "",
  department_id: "",
  doj: "",
  shift_hours: 9.0,
  shift_start: "09:00",
  duty_hrs_legacy: "",
  status: "Working",
  remark: "",
  overtime_applicable: true,
  extra_day_pay_on_off_work: false,
  credit_in: "Bank",
  uan: "",
  esi_code: "",
  ctc: 0,
  esi_applicable: false,
  pf_applicable: false,
  el_applicable: false,
  pl_applicable: false,
  short_early_applicable: false,
  short_late_applicable: false,
  wfh_applicable: false,
  od_applicable: false,
  bond: false,
  bond_duration_months: null,
  bank_name: "",
  account_no: "",
  ifsc: "",
  account_type: "Savings",
  probation_end_date: "",
  is_senior_management: false,
  // Phase 1D — extended taxonomy
  designation: "",
  sub_department: "",
  role: "employee",
};

// Designation suggestions (used by Combobox)
const DESIGNATION_SUGGESTIONS = [
  // Floor level
  "Operator", "Senior Operator", "Floor Supervisor", "Helper", "QC Operator",
  "Machine Operator", "Packer", "Loader",
  // Manager level
  "Production Manager", "QC Head", "Sales Manager", "Floor Manager",
  "Maintenance Head", "Store Keeper", "Accountant", "HR Manager",
  // Senior level
  "Senior Manager", "General Manager", "Director", "CEO/MD", "Founder & MD",
  "Co-Founder", "Production Head", "Sales Head",
];

const EMPLOYEE_ROLES = [
  { value: "employee", label: "Employee (Regular staff)" },
  { value: "manager", label: "Manager (Team lead — direct approver)" },
  { value: "hod", label: "HOD (Department Head)" },
  { value: "senior_management", label: "Senior Management (Owner / Director)" },
  { value: "ea", label: "EA (Executive Assistant — acts on behalf of Senior Mgmt)" },
];

const FieldRow = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">{children}</div>
);
const Field = ({ label, children, hint, required }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
      {label} {required && <span className="text-red-600">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
  </div>
);

export default function EmployeeForm() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = id && id !== "new";
  // Rev. 2026-02 — Include hr_admin & hr_maker (maker-checker roles).
  const canEdit = ["super_admin", "hr", "hr_admin", "hr_maker"].includes(user?.role);
  const canDelete = user?.role === "super_admin";

  const [form, setForm] = useState(empty);
  const [units, setUnits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // Phase B Session 5 — Onboard/Edit Success Card. When a submission
  // creates a PENDING approval request the form stays on-page and
  // renders a linkable card pointing to the /approvals inbox instead
  // of a terse toast.
  const [pendingApproval, setPendingApproval] = useState(null);
  const [tab, setTab] = useState("personal");
  // "Resigned" is a UI trigger, not a real employee.status value — it
  // opens this dialog which routes through the dedicated Exit
  // approval engine (POST /exits) instead of a plain Tier-1 field edit,
  // so notice period, F&F recovery estimate and Super Admin approval
  // are all captured per the exit module spec.
  const [exitDlgOpen, setExitDlgOpen] = useState(false);
  const [exitSubmitting, setExitSubmitting] = useState(false);
  const [exitForm, setExitForm] = useState({
    resignation_date: toInputDate(new Date().toISOString()),
    requested_lwd: "",
    notice_period_days: 30,
    exit_reason_category: "",
    notes: "",
  });
  // DOB / DOJ are locked by default on edit (existing employees) to prevent
  // accidental changes. Super Admin / HR can unlock-with-confirm to amend.
  const [dobLocked, setDobLocked] = useState(true);
  const [dojLocked, setDojLocked] = useState(true);

  // New-employee mode: dates are unlocked by default (HR is entering them now)
  useEffect(() => {
    if (!isEdit) {
      setDobLocked(false);
      setDojLocked(false);
    }
  }, [isEdit]);

  const unlockDob = () => {
    if (window.confirm(
      "Are you sure you want to unlock Date of Birth?\n\n" +
      "Changing this affects statutory records (PF, ESI, gratuity). " +
      "Only unlock if you are correcting an entry error."
    )) setDobLocked(false);
  };

  const unlockDoj = () => {
    if (window.confirm(
      "Are you sure you want to unlock Date of Joining?\n\n" +
      "Changing this affects probation, leave accrual, gratuity " +
      "eligibility and statutory records. Only unlock if you are " +
      "correcting an entry error."
    )) setDojLocked(false);
  };

  useEffect(() => {
    (async () => {
      const [u, d] = await Promise.all([api.get("/units"), api.get("/departments")]);
      setUnits(u.data);
      setDepartments(d.data);
      if (isEdit) {
        try {
          const { data } = await api.get(`/employees/${id}`);
          setForm({
            ...empty,
            ...data,
            dob: toInputDate(data.dob),
            doj: toInputDate(data.doj),
            probation_end_date: toInputDate(data.probation_end_date),
          });
        } catch {
          toast.error("Failed to load employee");
          navigate("/employees");
        }
      }
    })();
    // eslint-disable-next-line
  }, [id]);

  // auto-fill probation_end_date when DOJ changes
  useEffect(() => {
    if (form.doj && !form.probation_end_date) {
      const d = new Date(form.doj);
      if (!isNaN(d.getTime())) {
        d.setMonth(d.getMonth() + 6);
        setForm((f) => ({ ...f, probation_end_date: d.toISOString().slice(0, 10) }));
      }
    }
    // eslint-disable-next-line
  }, [form.doj]);

  const filteredDepts = useMemo(
    () => departments.filter((d) => d.unit_id === form.unit_id),
    [departments, form.unit_id]
  );

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Rev. 2026-08 — client-side helpers for punch_id + aadhaar validation
  const PUNCH_ID_RX = /^[A-Z]+-\d+$/;
  const punchIdWarning = (form.punch_id || "").trim() &&
    !PUNCH_ID_RX.test((form.punch_id || "").trim())
      ? "Format tip: use `PREFIX-NUMBER` (e.g. BPL-62). Non-standard formats " +
        "may fail biometric CSV parsing later."
      : null;
  const aadhaarDigits = String(form.aadhaar_no || "").replace(/\D/g, "");
  const aadhaarError = form.aadhaar_no !== undefined && form.aadhaar_no !== "" &&
    aadhaarDigits.length !== 12
      ? `Aadhaar must have exactly 12 digits (found ${aadhaarDigits.length})`
      : null;

  const submit = async () => {
    // Rev. 2026-08 — enforce mandatory Aadhaar client-side before hitting API
    if (aadhaarDigits.length !== 12) {
      toast.error(aadhaarDigits === ""
        ? "Aadhaar Number is required for every employee"
        : `Aadhaar must have exactly 12 digits (found ${aadhaarDigits.length})`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.bond_duration_months === "" || payload.bond_duration_months === null)
        payload.bond_duration_months = null;
      else payload.bond_duration_months = Number(payload.bond_duration_months);
      payload.ctc = Number(payload.ctc || 0);
      payload.shift_hours = Number(payload.shift_hours);
      // dates as ISO date strings (already YYYY-MM-DD from input)
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      // restore required strings
      payload.punch_id = form.punch_id;
      payload.employee_code = form.employee_code;
      payload.name = form.name;
      payload.unit_id = form.unit_id;
      payload.department_id = form.department_id;
      payload.doj = form.doj;
      payload.shift_start = form.shift_start || "09:00";
      payload.shift_hours = Number(form.shift_hours || 9);

      if (isEdit) {
        const res = await api.patch(`/employees/${id}`, payload);
        // Phase B Session 5 — if the PATCH created an approval
        // request (Tier-1 fields touched), show the linkable
        // pending-approval card instead of the terse toast.
        if (res.data?.approval_request_id) {
          setPendingApproval({
            id: res.data.approval_request_id,
            employeeName: form.name,
            employeeCode: form.employee_code,
            tier1Fields: res.data.tier1_pending_fields || [],
            tier2Fields: res.data.tier2_applied_fields || [],
            mode: "edit",
          });
          setSubmitting(false);
          return;  // stay on page, user clicks Continue
        }
        toast.success("Employee updated");
      } else {
        const res = await api.post("/employees", payload);
        // Phase B Session 5 — new employees always go to PENDING;
        // show the linkable card so HR can jump to /approvals.
        if (res.data?.approval_request_id) {
          setPendingApproval({
            id: res.data.approval_request_id,
            employeeName: form.name,
            employeeCode: form.employee_code,
            mode: "create",
          });
          setSubmitting(false);
          return;
        }
        toast.success("Employee added");
      }
      navigate("/employees");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete employee "${form.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Employee deleted");
      navigate("/employees");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submitExit = async () => {
    if (!exitForm.requested_lwd) {
      toast.error("Last Working Date is required");
      return;
    }
    setExitSubmitting(true);
    try {
      const { data } = await api.post("/exits", {
        employee_id: id,
        exit_type: "Resignation",
        resignation_date: exitForm.resignation_date,
        requested_lwd: exitForm.requested_lwd,
        notice_period_days: Number(exitForm.notice_period_days) || 0,
        exit_reason_category: exitForm.exit_reason_category || null,
        notes: exitForm.notes || null,
      });
      toast.success("Resignation submitted for Super Admin approval");
      setExitDlgOpen(false);
      setPendingApproval({
        mode: "exit",
        employeeName: form.name,
        id: data.approval_request_id,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit resignation");
    } finally {
      setExitSubmitting(false);
    }
  };

  return (
    <div data-testid="employee-form-page" className="space-y-6 max-w-6xl">
      {/* Persistent banner — derived from the loaded employee's own
          fields, so it survives page refresh / re-navigation (unlike
          the ephemeral "just submitted" card below). */}
      {!pendingApproval && isEdit && (form.approval_status === "PENDING_APPROVAL" || form.pending_edit_request_id) && (
        <div
          data-testid="persistent-pending-banner"
          className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex items-start gap-3"
        >
          <div className="rounded-full bg-amber-400 p-2 shrink-0">
            <CheckCircle2 className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold">
              Revision Pending
            </div>
            <p className="text-sm text-amber-900 mt-0.5">
              {form.approval_status === "PENDING_APPROVAL"
                ? "This is a new employee awaiting Super Admin approval — not yet active for payroll or portal access."
                : "One or more sensitive fields (e.g. salary, bank, identity) have a change awaiting Super Admin approval. Values shown below are the current LIVE values, not the proposed ones."}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-amber-400 text-amber-800 hover:bg-amber-100 shrink-0"
            onClick={() => navigate("/approvals")}
            data-testid="persistent-pending-view-btn"
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            View in Approvals
          </Button>
        </div>
      )}
      {/* Phase B Session 5 — Onboard/Edit Success Card (visible only
          after a submission that created a PENDING approval). */}
      {pendingApproval && (
        <div
          data-testid="pending-approval-card"
          className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-xl p-6 shadow-sm"
        >
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-emerald-500 p-3 shrink-0">
              <CheckCircle2 className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
                {pendingApproval.mode === "create"
                  ? "Submitted for Approval"
                  : pendingApproval.mode === "exit"
                  ? "Resignation Submitted"
                  : "Sensitive Changes Submitted"}
              </div>
              <h2
                className="text-xl font-bold text-slate-900 mt-1"
                style={{ fontFamily: "Chivo" }}
              >
                {pendingApproval.mode === "create"
                  ? `${pendingApproval.employeeName} is pending Super Admin review`
                  : pendingApproval.mode === "exit"
                  ? `${pendingApproval.employeeName}'s resignation is pending Super Admin review`
                  : `${pendingApproval.employeeName}: ${pendingApproval.tier1Fields.length} sensitive field(s) pending review`}
              </h2>
              <p className="text-sm text-slate-700 mt-1">
                {pendingApproval.mode === "create" ? (
                  <>The employee will become active for payroll, attendance and
                     portal access only after a Super Admin approves the request.</>
                ) : pendingApproval.mode === "exit" ? (
                  <>The employee stays "Working" until a Super Admin approves the
                     resignation and the last working date arrives — status then
                     flips to "Left" automatically.</>
                ) : (
                  <>
                    {pendingApproval.tier2Fields?.length > 0 && (
                      <>Non-sensitive fields ({pendingApproval.tier2Fields.join(", ")}) were saved immediately. </>
                    )}
                    Sensitive fields ({pendingApproval.tier1Fields.join(", ")}) are frozen
                    on the approval request and will apply only after Super Admin approval.
                  </>
                )}
              </p>
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={() => navigate(`/approvals?req=${pendingApproval.id}`)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="view-in-approvals-btn"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View in Approvals
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/employees")}
                  data-testid="back-to-employees-btn"
                >
                  Back to Employees
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setPendingApproval(null)}
                  data-testid="dismiss-approval-card-btn"
                >
                  Stay & Edit More
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Link to="/employees" className="text-slate-500 hover:text-slate-900">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">
            {isEdit ? "Edit Employee" : "New Employee"}
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            {form.name || "Untitled employee"}
          </h1>
        </div>
        {isEdit && canDelete && (
          <Button variant="outline" data-testid="delete-emp-btn" onClick={onDelete} className="text-red-700 border-red-300 hover:bg-red-50">
            <Trash2 className="h-4 w-4 mr-2" /> Delete
          </Button>
        )}
        {canEdit && (
          <Button data-testid="save-emp-btn" disabled={submitting} onClick={submit} className="brand-bg brand-bg-hover text-white">
            <Save className="h-4 w-4 mr-2" />
            {submitting ? "Saving..." : "Save"}
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200 p-1 rounded-md w-full overflow-x-auto justify-start">
          <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
          <TabsTrigger value="identity" data-testid="tab-identity">Identity</TabsTrigger>
          <TabsTrigger value="employment" data-testid="tab-employment">Employment</TabsTrigger>
          <TabsTrigger value="statutory" data-testid="tab-statutory">Statutory</TabsTrigger>
          <TabsTrigger value="bank" data-testid="tab-bank">Bank</TabsTrigger>
          {isEdit && (
            <TabsTrigger value="documents" data-testid="tab-documents">Photo &amp; Docs</TabsTrigger>
          )}
          {isEdit && (
            <TabsTrigger value="portal" data-testid="tab-portal">Portal Access</TabsTrigger>
          )}
        </TabsList>

        {/* PERSONAL */}
        <TabsContent value="personal" className="bg-white border border-slate-200 rounded-md p-6 mt-3">
          <FieldRow>
            <Field label="Full Name" required>
              <Input data-testid="f-name" value={form.name} onChange={(e) => update("name", e.target.value)} />
            </Field>
            <Field label="Father / Husband Name">
              <Input value={form.father_husband_name || ""} onChange={(e) => update("father_husband_name", e.target.value)} />
            </Field>
            <Field label="Mobile">
              <Input value={form.mobile || ""} onChange={(e) => update("mobile", e.target.value)} />
            </Field>
            <Field label="Date of Birth">
              <div className="flex items-center gap-1.5">
                <Input
                  type="date"
                  value={form.dob || ""}
                  onChange={(e) => update("dob", e.target.value)}
                  disabled={dobLocked}
                  className={dobLocked ? "bg-slate-50 text-slate-600" : ""}
                  data-testid="f-dob"
                />
                {dobLocked && (
                  <button
                    type="button"
                    onClick={unlockDob}
                    className="inline-flex items-center gap-1 px-2 py-1.5 text-[10px] font-medium border border-amber-300 bg-amber-50 text-amber-700 rounded hover:bg-amber-100 whitespace-nowrap"
                    data-testid="unlock-dob"
                    title="Click to unlock for correction"
                  >
                    <Lock className="h-3 w-3" /> Locked
                  </button>
                )}
                {!dobLocked && form.id && (
                  <button
                    type="button"
                    onClick={() => setDobLocked(true)}
                    className="inline-flex items-center gap-1 px-2 py-1.5 text-[10px] font-medium border border-emerald-300 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 whitespace-nowrap"
                    data-testid="lock-dob"
                    title="Re-lock to prevent edits"
                  >
                    <Unlock className="h-3 w-3" /> Unlocked
                  </button>
                )}
              </div>
            </Field>
            <Field label="Permanent Address">
              <Textarea rows={2} value={form.permanent_address || ""} onChange={(e) => update("permanent_address", e.target.value)} />
            </Field>
            <Field label="Communication Address">
              <Textarea rows={2} value={form.communication_address || ""} onChange={(e) => update("communication_address", e.target.value)} />
            </Field>
            <Field label="Emergency Contact Person">
              <Input value={form.emergency_contact_person || ""} onChange={(e) => update("emergency_contact_person", e.target.value)} />
            </Field>
            <Field label="Relation">
              <Input value={form.emergency_contact_relation || ""} onChange={(e) => update("emergency_contact_relation", e.target.value)} />
            </Field>
            <Field label="Emergency Number">
              <Input value={form.emergency_contact_number || ""} onChange={(e) => update("emergency_contact_number", e.target.value)} />
            </Field>
          </FieldRow>
        </TabsContent>

        {/* IDENTITY */}
        <TabsContent value="identity" className="bg-white border border-slate-200 rounded-md p-6 mt-3">
          <FieldRow>
            <Field label="Aadhaar Number" required hint="Mandatory. Stored encrypted; only last 4 visible in lists. Must be unique across all employees.">
              <Input
                data-testid="f-aadhaar"
                value={form.aadhaar_no || ""}
                onChange={(e) => update("aadhaar_no", e.target.value)}
                placeholder="1234-5678-9012"
                className={aadhaarError ? "border-red-400 focus-visible:ring-red-400" : ""}
              />
              {aadhaarError && (
                <p
                  className="text-[11px] text-red-600 mt-1"
                  data-testid="f-aadhaar-error"
                >
                  {aadhaarError}
                </p>
              )}
            </Field>
            <Field label="PAN Number" hint="Stored encrypted.">
              <Input data-testid="f-pan" value={form.pan_no || ""} onChange={(e) => update("pan_no", e.target.value.toUpperCase())} placeholder="ABCDE1234F" />
            </Field>
            <Field label="Photo URL (optional)">
              <Input value={form.photo_url || ""} onChange={(e) => update("photo_url", e.target.value)} placeholder="upload separately" />
            </Field>
          </FieldRow>
        </TabsContent>

        {/* EMPLOYMENT */}
        <TabsContent value="employment" className="bg-white border border-slate-200 rounded-md p-6 mt-3">
          <FieldRow>
            <Field label="Punch ID" required hint="Must match the EmpID printed in the biometric CSV. Format: PREFIX-NUMBER (e.g. BPL-62). Spaces will break attendance parsing.">
              <Input
                data-testid="f-punch-id"
                value={form.punch_id}
                onChange={(e) => update("punch_id", e.target.value)}
                placeholder="Paytime CSV Emp ID — e.g. BPL-62 or 5"
                className={punchIdWarning ? "border-amber-400 focus-visible:ring-amber-400" : ""}
              />
              {punchIdWarning && (
                <p
                  className="text-[11px] text-amber-700 mt-1 flex items-start gap-1"
                  data-testid="f-punch-id-warning"
                >
                  <span className="inline-block h-3 w-3 rounded-full bg-amber-400 flex-shrink-0 mt-0.5" />
                  <span>{punchIdWarning}</span>
                </p>
              )}
            </Field>
            <Field label="Employee Code" required>
              <Input data-testid="f-emp-code" value={form.employee_code} onChange={(e) => update("employee_code", e.target.value)} placeholder="BPL-321" />
            </Field>
            <Field label="Biometric Punch ID">
              <Input data-testid="f-biometric-id" value={form.biometric_id || ""} onChange={(e) => update("biometric_id", e.target.value)} placeholder="Device numeric e.g. 5 (Mantra Punch ID)" />
            </Field>
            <Field label="Unit" required>
              <Select value={form.unit_id || undefined} onValueChange={(v) => update("unit_id", v)}>
                <SelectTrigger data-testid="f-unit"><SelectValue placeholder="Select unit" /></SelectTrigger>
                <SelectContent>
                  {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Department" required>
              <Select value={form.department_id || undefined} onValueChange={(v) => update("department_id", v)} disabled={!form.unit_id}>
                <SelectTrigger data-testid="f-department"><SelectValue placeholder={form.unit_id ? "Select department" : "Select unit first"} /></SelectTrigger>
                <SelectContent>
                  {filteredDepts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Date of Joining" required>
              <div className="flex items-center gap-1.5">
                <Input
                  type="date"
                  data-testid="f-doj"
                  value={form.doj || ""}
                  onChange={(e) => update("doj", e.target.value)}
                  disabled={dojLocked}
                  className={dojLocked ? "bg-slate-50 text-slate-600" : ""}
                />
                {dojLocked && (
                  <button
                    type="button"
                    onClick={unlockDoj}
                    className="inline-flex items-center gap-1 px-2 py-1.5 text-[10px] font-medium border border-amber-300 bg-amber-50 text-amber-700 rounded hover:bg-amber-100 whitespace-nowrap"
                    data-testid="unlock-doj"
                    title="Click to unlock for correction"
                  >
                    <Lock className="h-3 w-3" /> Locked
                  </button>
                )}
                {!dojLocked && form.id && (
                  <button
                    type="button"
                    onClick={() => setDojLocked(true)}
                    className="inline-flex items-center gap-1 px-2 py-1.5 text-[10px] font-medium border border-emerald-300 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 whitespace-nowrap"
                    data-testid="lock-doj"
                    title="Re-lock to prevent edits"
                  >
                    <Unlock className="h-3 w-3" /> Unlocked
                  </button>
                )}
              </div>
            </Field>
            <Field label="Shift Hours" required>
              <Select value={String(form.shift_hours)} onValueChange={(v) => update("shift_hours", Number(v))}>
                <SelectTrigger data-testid="f-shift-hours"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SHIFT_OPTIONS.map((h) => <SelectItem key={h} value={String(h)}>{h.toFixed(1)} hrs</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Shift Start" required hint="24-hour HH:MM">
              <Input type="time" data-testid="f-shift-start" value={form.shift_start || "09:00"} onChange={(e) => update("shift_start", e.target.value)} />
            </Field>
            <Field label="Legacy Duty Hours">
              <Input value={form.duty_hrs_legacy || ""} onChange={(e) => update("duty_hrs_legacy", e.target.value)} placeholder="08:30" />
            </Field>
            <Field label="Status" required hint={isEdit && form.status === "Working" ? "Pick \"Resigned\" to start the exit approval workflow." : undefined}>
              <Select
                value={form.status}
                onValueChange={(v) => {
                  if (v === "Resigned") { setExitDlgOpen(true); return; }
                  update("status", v);
                }}
              >
                <SelectTrigger data-testid="f-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  {isEdit && form.status === "Working" && (
                    <SelectItem value="Resigned" data-testid="status-option-resigned">
                      Resigned…
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Designation" hint="Pick from common designations or choose 'Custom...' to type your own.">
              {(() => {
                const isCustom =
                  form.designation && !DESIGNATION_SUGGESTIONS.includes(form.designation);
                return (
                  <div className="space-y-2">
                    <Select
                      value={isCustom ? "__custom__" : (form.designation || "__none__")}
                      onValueChange={(v) => {
                        if (v === "__custom__") update("designation", " ");  // flip to custom mode
                        else if (v === "__none__") update("designation", "");
                        else update("designation", v);
                      }}
                    >
                      <SelectTrigger data-testid="f-designation"><SelectValue placeholder="Select designation" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="__none__">— None —</SelectItem>
                        {DESIGNATION_SUGGESTIONS.map((d) => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                        <SelectItem value="__custom__">+ Custom (type your own)</SelectItem>
                      </SelectContent>
                    </Select>
                    {isCustom && (
                      <Input
                        autoFocus
                        data-testid="f-designation-custom"
                        value={form.designation === " " ? "" : (form.designation || "")}
                        onChange={(e) => update("designation", e.target.value)}
                        placeholder="Type custom designation"
                      />
                    )}
                  </div>
                );
              })()}
            </Field>
            <Field label="Sub-Department" hint="Optional. e.g. Flexo, Die-cut, In-process QC.">
              <Input
                data-testid="f-sub-department"
                value={form.sub_department || ""}
                onChange={(e) => update("sub_department", e.target.value)}
                placeholder="(optional)"
              />
            </Field>
            <Field label="Role" required hint="Determines what this employee IS in the hierarchy (Manager / HOD / etc.). Email + reporting chain are set in the 'Portal Access' tab after saving.">
              <Select value={form.role || "employee"} onValueChange={(v) => update("role", v)}>
                <SelectTrigger data-testid="f-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EMPLOYEE_ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Credit In">
              <Select value={form.credit_in} onValueChange={(v) => update("credit_in", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bank">Bank</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Overtime Applicable">
              <div className="flex items-center gap-3 pt-2">
                <Switch checked={!!form.overtime_applicable} onCheckedChange={(v) => update("overtime_applicable", v)} />
                <span className="text-sm text-slate-600">{form.overtime_applicable ? "Yes" : "No"}</span>
              </div>
            </Field>
            <Field
              label="Extra Day Pay for Sunday/Holiday Work"
              hint="Non-OT employees (admin/production admin) get +1 day pay for physically working on a Sunday or public holiday. Blue-collar OT stays separate."
            >
              <div className="flex items-center gap-3 pt-2">
                <Switch
                  data-testid="f-extra-day-pay"
                  checked={!!form.extra_day_pay_on_off_work}
                  onCheckedChange={(v) => update("extra_day_pay_on_off_work", v)}
                />
                <span className="text-sm text-slate-600">{form.extra_day_pay_on_off_work ? "Yes — extra day counted" : "No"}</span>
              </div>
            </Field>
            <Field label="Remark">
              <Input value={form.remark || ""} onChange={(e) => update("remark", e.target.value)} />
            </Field>
          </FieldRow>
        </TabsContent>

        {/* STATUTORY */}
        <TabsContent value="statutory" className="bg-white border border-slate-200 rounded-md p-6 mt-3">
          <FieldRow>
            <Field label="UAN">
              <Input value={form.uan || ""} onChange={(e) => update("uan", e.target.value)} />
            </Field>
            <Field label="ESI Code">
              <Input value={form.esi_code || ""} onChange={(e) => update("esi_code", e.target.value)} />
            </Field>
            <Field label="CTC (Monthly INR)" required>
              <Input data-testid="f-ctc" type="number" value={form.ctc} onChange={(e) => update("ctc", e.target.value)} />
            </Field>
            {isEdit && (
              <Field label="Master Record">
                <Button
                  asChild
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-red-200 text-red-700 hover:bg-red-50 w-full justify-start"
                  data-testid="open-master-record-btn"
                >
                  <Link to={`/employees/${id}/master-record`}>
                    📄 Open Master Record
                  </Link>
                </Button>
              </Field>
            )}
            <Field label="ESI Applicable">
              <div className="flex items-center gap-3 pt-2">
                <Switch checked={!!form.esi_applicable} onCheckedChange={(v) => update("esi_applicable", v)} />
                <span className="text-sm text-slate-600">{form.esi_applicable ? "Yes" : "No"}</span>
              </div>
            </Field>
            <Field label="PF Applicable">
              <div className="flex items-center gap-3 pt-2">
                <Switch checked={!!form.pf_applicable} onCheckedChange={(v) => update("pf_applicable", v)} />
                <span className="text-sm text-slate-600">{form.pf_applicable ? "Yes" : "No"}</span>
              </div>
            </Field>
            <Field label="Bond">
              <div className="flex items-center gap-3 pt-2">
                <Switch checked={!!form.bond} onCheckedChange={(v) => update("bond", v)} />
                <span className="text-sm text-slate-600">{form.bond ? "Yes" : "No"}</span>
              </div>
            </Field>
            <Field label="Bond Duration (months)">
              <Select
                value={form.bond_duration_months ? String(form.bond_duration_months) : "none"}
                onValueChange={(v) => update("bond_duration_months", v === "none" ? null : Number(v))}
                disabled={!form.bond}
              >
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="6">6 months</SelectItem>
                  <SelectItem value="12">12 months</SelectItem>
                  <SelectItem value="18">18 months</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Bond Amount (₹ / month)" hint="Monthly deduction during bond period">
              <Input
                type="number"
                min="0"
                step="100"
                value={form.bond_amount ?? ""}
                onChange={(e) => update("bond_amount", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                disabled={!form.bond}
                data-testid="f-bond-amount"
                placeholder="0"
              />
            </Field>
            <Field label="Probation End Date" hint="Auto: DOJ + 6 months">
              <Input type="date" value={form.probation_end_date || ""} onChange={(e) => update("probation_end_date", e.target.value)} />
            </Field>
            <Field label="Senior Management" hint="Fixed salary regardless of attendance">
              <div className="flex items-center gap-3 pt-2">
                <Switch checked={!!form.is_senior_management} onCheckedChange={(v) => update("is_senior_management", v)} />
                <span className="text-sm text-slate-600">{form.is_senior_management ? "Yes" : "No"}</span>
              </div>
            </Field>
            <Field label="EL — Earned Leave" hint="Activates AFTER probation completes">
              <div className="flex items-center gap-3 pt-2">
                <Switch
                  checked={!!form.el_applicable}
                  onCheckedChange={(v) => update("el_applicable", v)}
                  data-testid="f-el-applicable"
                />
                <span className="text-sm text-slate-600">
                  {form.el_applicable ? (
                    (() => {
                      const doj = form.doj;
                      const months = form.probation_period_months || 6;
                      if (!doj) return "Eligible (probation status unknown)";
                      const end = new Date(doj);
                      if (isNaN(end.getTime())) {
                        return <span className="text-amber-700">Eligible (invalid DOJ — please fix)</span>;
                      }
                      end.setMonth(end.getMonth() + months);
                      const completed = new Date() >= end;
                      return completed
                        ? <span className="text-emerald-700 font-semibold">Active</span>
                        : <span className="text-amber-700">Pending probation (ends {end.toISOString().slice(0, 10)})</span>;
                    })()
                  ) : "Disabled"}
                </span>
              </div>
            </Field>
            <Field label="PL — Paid Leave" hint="Activates immediately">
              <div className="flex items-center gap-3 pt-2">
                <Switch
                  checked={!!form.pl_applicable}
                  onCheckedChange={(v) => update("pl_applicable", v)}
                  data-testid="f-pl-applicable"
                />
                <span className="text-sm text-slate-600">
                  {form.pl_applicable
                    ? <span className="text-emerald-700 font-semibold">Active</span>
                    : "Disabled"}
                </span>
              </div>
            </Field>
            <Field label="Short Leave — Early" hint="1 hr early leave, 2/month combined cap">
              <div className="flex items-center gap-3 pt-2">
                <Switch
                  checked={!!form.short_early_applicable}
                  onCheckedChange={(v) => update("short_early_applicable", v)}
                  data-testid="f-short-early-applicable"
                />
                <span className="text-sm text-slate-600">
                  {form.short_early_applicable ? "Available on portal" : "Hidden from portal"}
                </span>
              </div>
            </Field>
            <Field label="Short Leave — Late" hint="1 hr late arrival, 2/month combined cap">
              <div className="flex items-center gap-3 pt-2">
                <Switch
                  checked={!!form.short_late_applicable}
                  onCheckedChange={(v) => update("short_late_applicable", v)}
                  data-testid="f-short-late-applicable"
                />
                <span className="text-sm text-slate-600">
                  {form.short_late_applicable ? "Available on portal" : "Hidden from portal"}
                </span>
              </div>
            </Field>
            <Field label="Work from Home (WFH)" hint="Requires 24-hr advance notice">
              <div className="flex items-center gap-3 pt-2">
                <Switch
                  checked={!!form.wfh_applicable}
                  onCheckedChange={(v) => update("wfh_applicable", v)}
                  data-testid="f-wfh-applicable"
                />
                <span className="text-sm text-slate-600">
                  {form.wfh_applicable ? "Available on portal" : "Hidden from portal"}
                </span>
              </div>
            </Field>
            <Field label="On Duty (OD)" hint="Counts as present, requires customer location">
              <div className="flex items-center gap-3 pt-2">
                <Switch
                  checked={!!form.od_applicable}
                  onCheckedChange={(v) => update("od_applicable", v)}
                  data-testid="f-od-applicable"
                />
                <span className="text-sm text-slate-600">
                  {form.od_applicable ? "Available on portal" : "Hidden from portal"}
                </span>
              </div>
            </Field>
          </FieldRow>
        </TabsContent>

        {/* BANK */}
        <TabsContent value="bank" className="bg-white border border-slate-200 rounded-md p-6 mt-3">
          <FieldRow>
            <Field label="Bank Name">
              <Input value={form.bank_name || ""} onChange={(e) => update("bank_name", e.target.value)} />
            </Field>
            <Field label="Account Number" hint="Stored encrypted">
              <Input data-testid="f-account-no" value={form.account_no || ""} onChange={(e) => update("account_no", e.target.value)} />
            </Field>
            <Field label="IFSC" hint="Format: AAAA0XXXXXX">
              <Input data-testid="f-ifsc" value={form.ifsc || ""} onChange={(e) => update("ifsc", e.target.value.toUpperCase())} />
            </Field>
            <Field label="Account Type">
              <Select value={form.account_type || "Savings"} onValueChange={(v) => update("account_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Savings">Savings</SelectItem>
                  <SelectItem value="Current">Current</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </FieldRow>
        </TabsContent>

        {isEdit && (
          <TabsContent value="documents" className="bg-white border border-slate-200 rounded-md p-6 mt-3">
            <EmployeeDocumentsSection
              employee={form}
              canEdit={canEdit}
              canVerify={["super_admin", "hr", "hr_admin", "hr_maker"].includes(user?.role)}
              onPhotoChange={(path) => update("profile_photo_url", path)}
            />
          </TabsContent>
        )}

        {isEdit && (
          <TabsContent value="portal" className="bg-white border border-slate-200 rounded-md p-6 mt-3">
            <PortalAccessSection
              employee={form}
              canManage={canEdit}
              onChanged={async () => {
                // Refetch the employee to pick up portal changes
                const { data } = await api.get(`/employees/${id}`);
                setForm({
                  ...empty,
                  ...data,
                  dob: toInputDate(data.dob),
                  doj: toInputDate(data.doj),
                  probation_end_date: toInputDate(data.probation_end_date),
                });
              }}
            />
          </TabsContent>
        )}
      </Tabs>

      {/* Resigned → dedicated Exit approval workflow (POST /exits),
          not a plain Tier-1 status edit. */}
      <Dialog open={exitDlgOpen} onOpenChange={setExitDlgOpen}>
        <DialogContent data-testid="exit-dialog" className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserMinus className="h-4 w-4 text-red-600" />
              Submit Resignation — {form.name}
            </DialogTitle>
            <DialogDescription>
              This goes to Super Admin for approval. The employee stays
              "Working" until approved and the last working date arrives.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Resignation Date *</Label>
              <Input
                type="date"
                value={exitForm.resignation_date}
                onChange={(e) => setExitForm((f) => ({ ...f, resignation_date: e.target.value }))}
                className="h-8 mt-1 text-xs"
                data-testid="exit-resignation-date"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Last Working Date *</Label>
              <Input
                type="date"
                value={exitForm.requested_lwd}
                onChange={(e) => setExitForm((f) => ({ ...f, requested_lwd: e.target.value }))}
                className="h-8 mt-1 text-xs"
                data-testid="exit-lwd"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Notice Period (days)</Label>
              <Input
                type="number"
                min="0"
                value={exitForm.notice_period_days}
                onChange={(e) => setExitForm((f) => ({ ...f, notice_period_days: e.target.value }))}
                className="h-8 mt-1 text-xs"
                data-testid="exit-notice-days"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Reason Category</Label>
              <Input
                value={exitForm.exit_reason_category}
                onChange={(e) => setExitForm((f) => ({ ...f, exit_reason_category: e.target.value }))}
                placeholder="e.g. Higher studies, Better opportunity"
                className="h-8 mt-1 text-xs"
                data-testid="exit-reason-category"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase text-slate-500">Notes</Label>
              <Textarea
                rows={2}
                value={exitForm.notes}
                onChange={(e) => setExitForm((f) => ({ ...f, notes: e.target.value }))}
                className="mt-1 text-xs"
                data-testid="exit-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExitDlgOpen(false)}>Cancel</Button>
            <Button
              onClick={submitExit}
              disabled={exitSubmitting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="submit-exit-btn"
            >
              {exitSubmitting ? "Submitting…" : "Submit for Approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
